import { useContext, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'

import { LoginParams, UserHookBag } from './types'
import { UserContext } from './context'
import { createClient } from '@services/api'
import { updateUser } from './fetchers'
import { SignupDetails } from '@models/user'
import router from 'next/router'

interface UserHookOptions {
  redirectOnUnauthorized?: boolean
}

export function useUser(options?: UserHookOptions): UserHookBag {
  const queryClient = useQueryClient()
  const tokenHelpers = useContext(UserContext)

  const user = useQuery(
    'user',
    async () => {
      try {
        const { data } = await createClient(
          undefined,
          options?.redirectOnUnauthorized
        ).get('/users/me')

        return {
          id: data.id,
          firstName: data.firstName,
          lastName: data.lastName,
          name: data.name,
          email: data.email,
          occupation: data.occupation,
          acccountType: data.acccountType,
          image: data.image,
          phoneNumber: data.phoneNumber,
          partner: data.partner,
          affiliation: data.affiliation
        }
      } catch (error) {
        if (error.response.status === 401) {
          tokenHelpers.removeToken()
          if (options?.redirectOnUnauthorized) {
            void router.push('/login')
          }
        }

        return undefined
      }
    },
    {
      enabled: tokenHelpers.hasToken,
      retry: false
    }
  )

  const updateMutation = useMutation(updateUser, {
    onSuccess: async () => await queryClient.invalidateQueries('user')
  })

  useEffect(() => {
    void queryClient.invalidateQueries('user')
  }, [tokenHelpers.hasToken])

  const loginMutation = useMutation(async (variables: LoginParams) => {
    const { data } = await createClient().post('/users/login', variables)

    tokenHelpers.setToken(data.token)
  })

  const logoutMutation = useMutation(
    async (): Promise<void> => {
      await createClient().get('/users/logout')

      tokenHelpers.removeToken()
      localStorage.removeItem('current-report')
      localStorage.removeItem('default-report')

      void queryClient.removeQueries('user')
      void user.refetch()
    }
  )

  const registerMutation = useMutation(
    async (variables: Omit<SignupDetails, 'confirmPassword' | 'acceptTOC'>) => {
      const { data } = await createClient().post('/users/register', variables)

      tokenHelpers.setToken(data.token)
    },
    {
      onSuccess: async () => await queryClient.invalidateQueries('user')
    }
  )

  return {
    user,
    isLoggedIn: tokenHelpers.hasToken,
    updateInfo: updateMutation,
    login: loginMutation,
    logout: logoutMutation,
    register: registerMutation
  }
}
